import { useEffect, useRef, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import Seo from "@/components/Seo";
import { Loader2, Upload, Trash2, ArrowLeft, Mail, Phone, LogOut, Star, Send, Check, Printer, MailOpen } from "lucide-react";
import { PROPERTIES } from "@/data/site";
import {
  getPhotos, uploadPhoto, deletePhoto, getEnquiries, fetchMe, clearToken,
  getAllReviews, setReviewPublished, setReviewRead, deleteReview, requestReview,
  setEnquiryRead, setEnquiryReplied, deleteEnquiry, exchangeGoogleSession, logoutSession, photoSrc,
  deleteReviewPhoto, reviewPhotoToGallery, fileUrl, setPhotoCaption,
} from "@/lib/api";
import OwnerLogin from "@/components/OwnerLogin";
import PropertyEditor from "@/components/PropertyEditor";
import OwnerAvailability from "@/components/OwnerAvailability";
import EnquiryReply from "@/components/EnquiryReply";
import OwnerEnquiryStats from "@/components/OwnerEnquiryStats";
import OwnerAlertBanner, { OwnerOverdueBanner } from "@/components/OwnerAlertBanner";
import { OwnerPhotoImport, OwnerOrphanPhotos } from "@/components/OwnerPhotoTools";

// REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
const GoogleCallback = ({ sessionId }) => {
  const navigate = useNavigate();
  const done = useRef(false);

  useEffect(() => {
    if (done.current) return;
    done.current = true;
    exchangeGoogleSession(sessionId)
      .then(({ data }) => {
        toast.success(`Signed in as ${data.user.email}`);
        navigate("/owner", { replace: true });
      })
      .catch((err) => {
        const detail = err.response?.data?.detail;
        toast.error(typeof detail === "string" ? detail : "Google sign-in failed. Please try again.");
        navigate("/owner", { replace: true });
      });
  }, [sessionId, navigate]);

  return (
    <div className="min-h-screen bg-[#FAFAFA] flex items-center justify-center" data-testid="owner-oauth-callback">
      <Loader2 size={22} className="animate-spin text-[#0F2A52]" />
    </div>
  );
};

export default function OwnerRoute() {
  const location = useLocation();
  // Detect the one-time session_id synchronously during render, before any auth check runs
  const sessionId = location.hash?.includes("session_id=")
    ? new URLSearchParams(location.hash.slice(1)).get("session_id")
    : null;

  if (sessionId) return <GoogleCallback sessionId={sessionId} />;
  return <Owner />;
}

function Owner() {
  const [authed, setAuthed] = useState(null);
  const [tab, setTab] = useState("photos");
  const [slug, setSlug] = useState(PROPERTIES[0].slug);
  const [photos, setPhotos] = useState([]);
  const [enquiries, setEnquiries] = useState([]);
  const [reviews, setReviews] = useState([]);
  const [busy, setBusy] = useState(false);
  const [photoKey, setPhotoKey] = useState(0);
  const [photosLoading, setPhotosLoading] = useState(false);
  const slugRef = useRef(slug);
  slugRef.current = slug;
  const visiblePhotos = photos.filter((p) => p.property_slug === slug);
  const unread = enquiries.filter((e) => !e.is_read).length;

  // Always clears the loading flag; the grid additionally filters by the selected property,
  // so a slow or out-of-order response can never show one property's photos under another's name
  const loadPhotos = (s) => {
    setPhotosLoading(true);
    return getPhotos(s)
      .then((r) => {
        if (slugRef.current === s) setPhotos(r.data);
      })
      .catch(() => {
        if (slugRef.current === s) setPhotos([]);
      })
      .finally(() => setPhotosLoading(false));
  };
  const loadEnquiries = () => getEnquiries().then((r) => setEnquiries(r.data)).catch(() => setEnquiries([]));
  const loadReviews = () => getAllReviews().then((r) => setReviews(r.data)).catch(() => setReviews([]));

  const askForReview = async (e) => {
    try {
      await requestReview({
        name: e.name,
        email: e.email,
        property_slug: e.property_slug || PROPERTIES[0].slug,
        property_name: e.property_name || PROPERTIES[0].name,
      });
      toast.success(`Review request emailed to ${e.name}`);
    } catch {
      toast.error("Could not send the review request");
    }
  };

  const markReplied = async (e, replied) => {
    try {
      await setEnquiryReplied(e.id, replied);
      setEnquiries((list) =>
        list.map((x) => (x.id === e.id ? { ...x, replied, is_read: replied ? true : x.is_read } : x))
      );
      if (replied) toast.success(`Marked as replied — reminders for ${e.name} will stop`);
    } catch {
      toast.error("Could not update that enquiry");
    }
  };

  const toggleRead = async (e) => {
    try {
      await setEnquiryRead(e.id, !e.is_read);
      setEnquiries((list) => list.map((x) => (x.id === e.id ? { ...x, is_read: !x.is_read } : x)));
    } catch {
      toast.error("Could not update that enquiry");
    }
  };

  const removeEnquiry = async (e) => {
    if (!window.confirm(`Delete the enquiry from ${e.name}? It will be removed from this list.`)) return;
    try {
      await deleteEnquiry(e.id);
      setEnquiries((list) => list.filter((x) => x.id !== e.id));
      toast.success("Enquiry deleted");
    } catch {
      toast.error("Could not delete that enquiry");
    }
  };

  const togglePublished = async (r) => {
    try {
      await setReviewPublished(r.id, !r.is_published);
      setReviews((list) => list.map((x) => (x.id === r.id ? { ...x, is_published: !x.is_published } : x)));
      toast.success(r.is_published ? "Review hidden" : "Review published");
    } catch {
      toast.error("Could not update the review");
    }
  };

  const toggleReviewRead = async (r) => {
    try {
      await setReviewRead(r.id, !r.is_read);
      setReviews((list) => list.map((x) => (x.id === r.id ? { ...x, is_read: !x.is_read } : x)));
    } catch {
      toast.error("Could not update the review");
    }
  };

  const removeReview = async (r) => {    if (!window.confirm(`Delete the review from ${r.guest_name}? This cannot be undone.`)) return;
    try {
      await deleteReview(r.id);
      setReviews((list) => list.filter((x) => x.id !== r.id));
      toast.success("Review deleted");
    } catch {
      toast.error("Could not delete that review");
    }
  };

  const removeReviewPhoto = async (r, path) => {
    if (!window.confirm("Remove this guest photo? It will no longer show with the review.")) return;
    try {
      await deleteReviewPhoto(r.id, path);
      setReviews((list) =>
        list.map((x) => (x.id === r.id ? { ...x, photos: (x.photos || []).filter((p) => p !== path) } : x))
      );
      toast.success("Photo removed");
    } catch {
      toast.error("Could not remove that photo");
    }
  };

  const addPhotoToGallery = async (r, path) => {
    try {
      await reviewPhotoToGallery(r.id, path);
      toast.success(`Added to the ${r.property_name} gallery`);
    } catch {
      toast.error("Could not add that photo to the gallery");
    }
  };

  const saveCaption = async (photo, caption) => {
    const next = caption.trim();
    if ((photo.caption || "") === next) return;
    try {
      await setPhotoCaption(photo.id, next);
      photo.caption = next;
      toast.success(next ? "Caption saved" : "Caption removed");
    } catch {
      toast.error("Could not save that caption");
    }
  };

  useEffect(() => {
    document.title = "Owner area | PARADISIO RESORTS";
    // Works for both sign-in methods: bearer token if present, otherwise the httpOnly session cookie
    fetchMe()
      .then(() => setAuthed(true))
      .catch(() => {
        clearToken();
        setAuthed(false);
      });
  }, []);

  useEffect(() => {
    if (authed) loadPhotos(slug);
  }, [slug, authed]);

  useEffect(() => {
    if (authed) loadEnquiries();
    if (authed) loadReviews();
  }, [authed]);

  const signOut = async () => {
    clearToken();
    try {
      await logoutSession();
    } catch {
      // cookie may already be gone
    }
    setAuthed(false);
    setEnquiries([]);
    setPhotos([]);
  };

  if (authed === null)
    return (
      <div className="min-h-screen bg-[#FAFAFA]" data-testid="owner-loading">
        <Seo title="PARADISIO RESORTS | Owner area" description="Private owner area." noindex />
      </div>
    );
  if (!authed)
    return (
      <>
        <Seo title="PARADISIO RESORTS | Owner sign in" description="Private owner area." noindex />
        <OwnerLogin onSuccess={() => setAuthed(true)} />
      </>
    );

  const onFiles = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    setBusy(true);
    let ok = 0;
    for (const f of files) {
      try {
        await uploadPhoto(slug, f);
        ok += 1;
      } catch (err) {
        if (err.response?.status === 401) {
          clearToken();
          setAuthed(false);
          toast.error("Your session expired — please sign in again and re-upload");
          setBusy(false);
          return;
        }
        toast.error(`${f.name}: ${err.response?.data?.detail || "upload failed"}`);
      }
    }
    await loadPhotos(slug);
    setBusy(false);
    e.target.value = "";
    if (ok) toast.success(`${ok} photo${ok === 1 ? "" : "s"} uploaded`);
  };

  const onDelete = async (id) => {
    try {
      await deletePhoto(id);
      setPhotos((p) => p.filter((x) => x.id !== id));
      toast.success("Photo removed");
    } catch {
      toast.error("Could not remove photo");
    }
  };

  return (
    <div className="min-h-screen bg-[#FAFAFA] text-[#111111]" data-testid="owner-page">
      <Seo title="PARADISIO RESORTS | Owner area" description="Private owner area." noindex />
      <div className="max-w-6xl mx-auto px-6 py-10">
        <div className="flex flex-wrap items-center justify-between gap-4">
          <div>
            <h1 className="text-2xl font-medium" style={{ fontFamily: "Outfit, sans-serif" }}>
              Owner area
            </h1>
            <p className="text-sm text-neutral-500 mt-1">
              Upload photos, edit descriptions, block out booked dates, reply to enquiries, publish reviews
              and print guest sheets.
            </p>
          </div>
          <div className="flex items-center gap-6">
            <Link to="/" className="inline-flex items-center gap-2 text-sm text-neutral-600" data-testid="owner-back-link">
              <ArrowLeft size={15} /> Back to website
            </Link>
            <button
              onClick={signOut}
              className="inline-flex items-center gap-2 text-sm text-neutral-600"
              data-testid="owner-signout"
            >
              <LogOut size={15} /> Sign out
            </button>
          </div>
        </div>

        <div className="flex flex-wrap gap-2 mt-8">
          {[
            ["photos", "Photos"],
            ["details", "Descriptions"],
            ["enquiries", `Enquiries (${unread ? `${unread} new` : enquiries.length})`],
            ["availability", "Availability"],
            ["reviews", `Reviews (${reviews.length})`],
            ["print", "Printable sheets"],
          ].map(([k, label]) => (
            <button
              key={k}
              onClick={() => {
                setTab(k);
                if (k === "enquiries") loadEnquiries();
                if (k === "reviews") loadReviews();
              }}
              className={`px-4 py-2 rounded-md text-sm border ${
                tab === k ? "bg-[#0F2A52] text-white border-[#0F2A52]" : "bg-white border-[#E5E5E5] text-neutral-600"
              }`}
              data-testid={`owner-tab-${k}`}
            >
              {label}
            </button>
          ))}
        </div>

        {tab === "photos" && (
          <div className="mt-8 grid gap-4">
            <OwnerPhotoImport key={`import-${photoKey}`} onDone={() => { setPhotoKey((k) => k + 1); loadPhotos(slugRef.current); }} />
            <OwnerOrphanPhotos refreshKey={photoKey} onChange={() => setPhotoKey((k) => k + 1)} />
            <div className="bg-white border border-[#E5E5E5] rounded-md p-5">
              <label className="block text-xs uppercase tracking-wider text-neutral-500 mb-2">Property</label>
              <select
                value={slug}
                onChange={(e) => setSlug(e.target.value)}
                className="w-full md:w-96 border border-[#E5E5E5] rounded-md px-3 py-2 text-sm bg-white"
                data-testid="owner-property-select"
              >
                {PROPERTIES.map((p) => (
                  <option key={p.slug} value={p.slug}>
                    {p.name}
                  </option>
                ))}
              </select>

              <div className="mt-5">
                <label
                  className="inline-flex items-center gap-2 bg-[#0F2A52] text-white px-4 py-2.5 rounded-md text-sm cursor-pointer"
                  data-testid="owner-upload-label"
                >
                  {busy ? <Loader2 size={15} className="animate-spin" /> : <Upload size={15} />}
                  {busy ? "Uploading…" : "Upload photos"}
                  <input
                    type="file"
                    accept="image/*,.heic,.heif"
                    multiple
                    className="hidden"
                    onChange={onFiles}
                    data-testid="owner-upload-input"
                  />
                </label>
                <p className="text-xs text-neutral-500 mt-3">
                  JPG, PNG, HEIC (iPhone) or WEBP, up to 40MB each. Large photos are resized automatically and
                  appear at the end of that property's gallery straight away.
                </p>
              </div>
            </div>

            <div className="bg-white border border-[#E5E5E5] rounded-md p-5">
              <p className="text-sm text-neutral-500 mb-4">
                {photosLoading ? "Loading photos…" : `${visiblePhotos.length} photo(s)`}
                {!photosLoading && visiblePhotos.length > 0 && (
                  <span className="block text-xs text-neutral-400 mt-1">
                    Add a caption under any photo (like "sunrise from the deck") — guests see it when they open
                    that photo on the property page. Only photos listed here can have captions, so use "Import
                    built-in website photos" above first if a property is still showing its original photos.
                  </span>
                )}
              </p>
              <div
                key={slug}
                className={`grid grid-cols-2 md:grid-cols-4 gap-4 transition-opacity ${
                  photosLoading ? "opacity-40 pointer-events-none" : ""
                }`}
                data-testid="owner-photo-grid"
              >
                {visiblePhotos.map((p) => (
                  <div key={p.id} className="relative group border border-[#E5E5E5] rounded-md overflow-hidden">
                    <img src={photoSrc(p)} alt={p.original_filename} className="w-full h-32 object-cover" />
                    <button
                      onClick={() => onDelete(p.id)}
                      className="absolute top-2 right-2 bg-white/90 p-1.5 rounded-md"
                      data-testid={`owner-delete-photo-${p.id}`}
                      aria-label="Remove photo"
                    >
                      <Trash2 size={14} className="text-red-600" />
                    </button>
                    <input
                      defaultValue={p.caption || ""}
                      placeholder="Add a caption…"
                      maxLength={160}
                      onBlur={(e) => saveCaption(p, e.target.value)}
                      className="w-full border-t border-[#E5E5E5] px-2.5 py-2 text-xs bg-white focus:outline-none"
                      data-testid={`owner-photo-caption-${p.id}`}
                    />
                  </div>
                ))}
                {!visiblePhotos.length && !photosLoading && (
                  <p className="text-sm text-neutral-400">No uploads yet for this property.</p>
                )}
              </div>
            </div>
          </div>
        )}

        {tab === "details" && <PropertyEditor />}

        {tab === "enquiries" && (
          <div className="mt-8">
            <OwnerAlertBanner enquiries={enquiries} />
            <OwnerOverdueBanner enquiries={enquiries} onMarkReplied={(e) => markReplied(e, true)} />
            <OwnerEnquiryStats enquiries={enquiries} />
          </div>
        )}

        {tab === "enquiries" && (
          <div className="mt-8 bg-white border border-[#E5E5E5] rounded-md divide-y divide-[#E5E5E5]" data-testid="owner-enquiry-list">
            {enquiries.map((e) => (
              <div
                key={e.id}
                className={`p-5 ${e.is_read ? "" : "bg-[#FFFCF3] border-l-4 border-[#B08A3E]"}`}
                data-testid={`owner-enquiry-${e.id}`}
              >
                <div className="flex flex-wrap justify-between gap-3">
                  <p className="text-sm font-medium">
                    {e.name} · <span className="text-neutral-500">{e.property_name || "General enquiry"}</span>
                    {e.replied && (
                      <span className="ml-2 text-[10px] uppercase tracking-wider text-emerald-700">Replied</span>
                    )}
                  </p>
                  <p className="text-xs text-neutral-400">{new Date(e.created_at).toLocaleString()}</p>
                </div>
                <div className="flex flex-wrap gap-5 mt-3 text-xs text-neutral-600">
                  <a href={`mailto:${e.email}`} className="inline-flex items-center gap-1.5">
                    <Mail size={13} /> {e.email}
                  </a>
                  {e.phone && (
                    <a href={`tel:${e.phone}`} className="inline-flex items-center gap-1.5">
                      <Phone size={13} /> {e.phone}
                    </a>
                  )}
                  <span>
                    {e.check_in || "—"} → {e.check_out || "—"} · {e.guests} guest(s)
                  </span>
                </div>
                {e.message && <p className="text-sm text-neutral-700 mt-3 leading-relaxed">{e.message}</p>}
                <EnquiryReply enquiry={e} onReplied={(x) => markReplied(x, true)} />
                <div className="flex flex-wrap gap-2 mt-3">
                  <button
                    onClick={() => markReplied(e, !e.replied)}
                    className={`inline-flex items-center gap-2 text-sm rounded-md px-4 py-2.5 border ${
                      e.replied
                        ? "bg-emerald-50 border-emerald-300 text-emerald-800"
                        : "border-[#E5E5E5] text-[#0F2A52]"
                    }`}
                    data-testid={`owner-replied-${e.id}`}
                  >
                    <Check size={14} /> {e.replied ? "Replied — click to undo" : "Mark replied"}
                  </button>
                  <button
                    onClick={() => toggleRead(e)}
                    className="inline-flex items-center gap-2 text-sm text-[#0F2A52] border border-[#E5E5E5] rounded-md px-4 py-2.5"
                    data-testid={`owner-toggle-read-${e.id}`}
                  >
                    {e.is_read ? <MailOpen size={14} /> : <Check size={14} />}
                    {e.is_read ? "Mark as unread" : "Mark as done"}
                  </button>
                  <button
                    onClick={() => askForReview(e)}
                    className="inline-flex items-center gap-2 text-sm text-[#0F2A52] border border-[#E5E5E5] rounded-md px-4 py-2.5"
                    data-testid={`owner-request-review-${e.id}`}
                  >
                    <Send size={14} /> Ask for a review
                  </button>
                  <button
                    onClick={() => removeEnquiry(e)}
                    className="inline-flex items-center gap-2 text-sm text-red-600 border border-[#E5E5E5] rounded-md px-4 py-2.5"
                    data-testid={`owner-delete-enquiry-${e.id}`}
                  >
                    <Trash2 size={14} /> Delete
                  </button>
                </div>
              </div>
            ))}
            {!enquiries.length && <p className="p-5 text-sm text-neutral-400">No enquiries yet.</p>}
          </div>
        )}

        {tab === "availability" && <OwnerAvailability />}

        {tab === "print" && (
          <div className="mt-8 bg-white border border-[#E5E5E5] rounded-md p-5" data-testid="owner-print-panel">
            <h2 className="text-lg font-medium">Printable sheets</h2>
            <p className="text-sm text-neutral-600 mt-2 leading-relaxed">
              One-page summaries you can print or save as a PDF to leave with guests. Each sheet has a QR code
              that opens the property page on a phone. Open a sheet, then use your browser's Print option.
            </p>
            <div className="grid gap-3 sm:grid-cols-2 mt-5">
              <a
                href="/handout"
                target="_blank"
                rel="noreferrer"
                className="inline-flex items-center gap-2 bg-[#0F2A52] text-white rounded-md px-4 py-3 text-base"
                data-testid="owner-print-all"
              >
                <Printer size={16} /> All four homes (one sheet)
              </a>
              {PROPERTIES.map((p) => (
                <a
                  key={p.slug}
                  href={`/handout/${p.slug}`}
                  target="_blank"
                  rel="noreferrer"
                  className="inline-flex items-center gap-2 border border-[#E5E5E5] rounded-md px-4 py-3 text-base text-[#0F2A52]"
                  data-testid={`owner-print-${p.slug}`}
                >
                  <Printer size={16} /> {p.name}
                </a>
              ))}
            </div>
          </div>
        )}

        {tab === "reviews" && (
          <div className="mt-8 bg-white border border-[#E5E5E5] rounded-md divide-y divide-[#E5E5E5]" data-testid="owner-review-list">
            <p className="p-5 text-xs text-neutral-500">
              Reviews arrive here after a guest uses the "Ask for a review" link. Publish one to show it on
              that property's page.
            </p>
            {reviews.map((r) => (
              <div
                key={r.id}
                className={`p-5 ${r.is_read ? "" : "bg-[#FFFCF3] border-l-4 border-[#B08A3E]"}`}
                data-testid={`owner-review-${r.id}`}
              >
                <div className="flex flex-wrap justify-between gap-3">
                  <p className="text-sm font-medium">
                    {r.guest_name}
                    {r.guest_suburb ? `, ${r.guest_suburb}` : ""} ·{" "}
                    <span className="text-neutral-500">{r.property_name}</span>
                    {!r.is_read && (
                      <span className="ml-2 text-[10px] uppercase tracking-wider text-[#B08A3E]">New</span>
                    )}
                  </p>
                  <span className="inline-flex items-center gap-1 text-xs text-[#B08A3E]">
                    <Star size={13} className="fill-[#B08A3E]" /> {r.rating}/5
                  </span>
                </div>
                <p className="text-sm text-neutral-700 mt-3 leading-relaxed">{r.comment}</p>
                {(r.photos || []).length > 0 && (
                  <div className="flex flex-wrap gap-3 mt-4" data-testid={`owner-review-photos-${r.id}`}>
                    {r.photos.map((p, i) => (
                      <div key={p} className="w-28 border border-[#E5E5E5] rounded-md overflow-hidden">
                        <div className="relative">
                          <img src={fileUrl(p)} alt="Guest photo" className="w-full h-20 object-cover" />
                          <button
                            onClick={() => removeReviewPhoto(r, p)}
                            className="absolute top-1.5 right-1.5 bg-white/90 p-1 rounded-md"
                            aria-label="Remove guest photo"
                            data-testid={`owner-review-photo-delete-${r.id}-${i}`}
                          >
                            <Trash2 size={12} className="text-red-600" />
                          </button>
                        </div>
                        <button
                          onClick={() => addPhotoToGallery(r, p)}
                          className="w-full border-t border-[#E5E5E5] px-2 py-1.5 text-[11px] text-[#0F2A52]"
                          data-testid={`owner-review-photo-gallery-${r.id}-${i}`}
                        >
                          Add to gallery
                        </button>
                      </div>
                    ))}
                  </div>
                )}
                <div className="flex flex-wrap gap-2 mt-4">
                  <button
                    onClick={() => togglePublished(r)}
                    className={`text-xs rounded-md px-3 py-2 border ${
                      r.is_published ? "bg-[#0F2A52] text-white border-[#0F2A52]" : "border-[#E5E5E5] text-neutral-600"
                    }`}
                    data-testid={`owner-toggle-review-${r.id}`}
                  >
                    {r.is_published ? "Published — click to hide" : "Publish on website"}
                  </button>
                  <button
                    onClick={() => toggleReviewRead(r)}
                    className="inline-flex items-center gap-1.5 text-xs rounded-md px-3 py-2 border border-[#E5E5E5] text-neutral-600"
                    data-testid={`owner-review-read-${r.id}`}
                  >
                    {r.is_read ? <MailOpen size={13} /> : <Check size={13} />}
                    {r.is_read ? "Mark unread" : "Mark read"}
                  </button>
                  <button
                    onClick={() => removeReview(r)}
                    className="inline-flex items-center gap-1.5 text-xs rounded-md px-3 py-2 border border-red-200 text-red-700"
                    data-testid={`owner-delete-review-${r.id}`}
                  >
                    <Trash2 size={13} /> Delete
                  </button>
                </div>
              </div>
            ))}
            {!reviews.length && <p className="p-5 text-sm text-neutral-400">No reviews yet.</p>}
          </div>
        )}
      </div>
    </div>
  );
}
